import { useEffect, useState } from "react";
import { apiClient } from "../lib/api";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { LogOut, Lock, Trash2, Pencil } from "lucide-react";

const TOKEN_KEY = "rachfest_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => window.sessionStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [rsvps, setRsvps] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});

  const login = async (e) => {
    e.preventDefault();
    try {
      const res = await apiClient.post("/admin/login", { password });
      window.sessionStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
      setPassword("");
    } catch {
      toast.error("Wrong password");
    }
  };

  const logout = () => {
    window.sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
  };

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        apiClient.get(`/admin/rsvps?token=${encodeURIComponent(token)}`),
        apiClient.get(`/admin/rooms?token=${encodeURIComponent(token)}`),
      ]);
      setRsvps(r1.data);
      setRooms(r2.data);
    } catch {
      toast.error("Session expired");
      logout();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load();
  }, [token]);

  const updateStatus = async (room_id, status) => {
    try {
      await apiClient.post(`/admin/rooms/${room_id}/status?token=${encodeURIComponent(token)}&status=${status}`);
      toast.success("Updated");
      load();
    } catch {
      toast.error("Failed");
    }
  };

  const deleteRsvp = async (rsvp_id, guest_name) => {
    if (!window.confirm(`Delete RSVP from ${guest_name}? This cannot be undone.`)) return;
    try {
      await apiClient.delete(`/admin/rsvps/${rsvp_id}?token=${encodeURIComponent(token)}`);
      toast.success("RSVP deleted");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const openEdit = (r) => {
    setEditing(r);
    setEditForm({
      guest_name: r.guest_name || "",
      partner_name: r.partner_name || "",
      email: r.email || "",
      status: r.status || "yes",
      lodging: r.lodging || "tbd",
      dietary: r.dietary || "",
      notes: r.notes || "",
    });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await apiClient.patch(
        `/admin/rsvps/${editing.id}?token=${encodeURIComponent(token)}`,
        editForm
      );
      toast.success("RSVP updated");
      setEditing(null);
      load();
    } catch {
      toast.error("Failed to update");
    }
  };

  if (!token) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-6" data-testid="admin-login">
        <form onSubmit={login} className="max-w-sm w-full bg-sand border border-shell rounded-lg p-8">
          <Lock className="w-8 h-8 text-terracotta mb-4" />
          <h2 className="font-display text-3xl font-black text-ink mb-2">Admin</h2>
          <p className="text-muted_ink text-sm mb-6">For Rachel&apos;s eyes only.</p>
          <Label>Password</Label>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="bg-cream h-12 mt-2" data-testid="admin-password" />
          <button className="btn-primary w-full h-12 mt-5" data-testid="admin-login-btn">Enter</button>
        </form>
      </div>
    );
  }

  const roomsByType = rooms.reduce((acc, r) => {
    acc[r.room_type] = acc[r.room_type] || [];
    acc[r.room_type].push(r);
    return acc;
  }, {});

  const stats = {
    yes: rsvps.filter((r) => r.status === "yes").length,
    maybe: rsvps.filter((r) => r.status === "maybe").length,
    no: rsvps.filter((r) => r.status === "no").length,
    total: rsvps.length,
    open: rooms.filter((r) => r.status === "open").length,
    pending: rooms.filter((r) => r.status === "pending").length,
    confirmed: rooms.filter((r) => r.status === "confirmed").length,
  };

  return (
    <div className="min-h-screen bg-cream py-10">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex items-start justify-between mb-10 flex-wrap gap-4">
          <div>
            <p className="text-xs tracking-[0.4em] uppercase text-terracotta font-medium mb-2">Dashboard</p>
            <h1 className="font-display text-4xl md:text-5xl font-black text-ink">Admin</h1>
          </div>
          <button onClick={logout} className="btn-ghost" data-testid="admin-logout-btn">
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-7 gap-3 mb-8" data-testid="admin-stats">
          {[
            { l: "Total RSVPs", v: stats.total, c: "text-ink" },
            { l: "Yes", v: stats.yes, c: "text-sage-dark" },
            { l: "Maybe", v: stats.maybe, c: "text-gold" },
            { l: "No", v: stats.no, c: "text-muted_ink" },
            { l: "Rooms open", v: stats.open, c: "text-sage-dark" },
            { l: "Pending", v: stats.pending, c: "text-terracotta" },
            { l: "Confirmed", v: stats.confirmed, c: "text-ink" },
          ].map((s) => (
            <div key={s.l} className="bg-sand border border-shell rounded-md p-4">
              <p className="text-[10px] uppercase tracking-widest text-muted_ink">{s.l}</p>
              <p className={`font-display text-3xl font-black tabular-nums ${s.c}`}>{s.v}</p>
            </div>
          ))}
        </div>

        <Tabs defaultValue="rsvps">
          <TabsList className="bg-sand">
            <TabsTrigger value="rsvps" data-testid="tab-rsvps">RSVPs</TabsTrigger>
            <TabsTrigger value="rooms" data-testid="tab-rooms">Rooms</TabsTrigger>
          </TabsList>

          <TabsContent value="rsvps" className="mt-6">
            <div className="bg-sand border border-shell rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="rsvps-table">
                  <thead className="bg-ink text-cream">
                    <tr>
                      <th className="text-left p-3">Name</th>
                      <th className="text-left p-3">+1</th>
                      <th className="text-left p-3">Email</th>
                      <th className="text-left p-3">Status</th>
                      <th className="text-left p-3">Lodging</th>
                      <th className="text-left p-3">Dietary</th>
                      <th className="text-left p-3">Notes</th>
                      <th className="text-left p-3">Date</th>
                      <th className="text-left p-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rsvps.length === 0 && (
                      <tr><td colSpan={9} className="p-8 text-center text-muted_ink">No RSVPs yet</td></tr>
                    )}
                    {rsvps.map((r) => (
                      <tr key={r.id} className="border-t border-shell" data-testid={`rsvp-row-${r.id}`}>
                        <td className="p-3 font-medium text-ink">{r.guest_name}</td>
                        <td className="p-3 text-muted_ink">{r.partner_name || "-"}</td>
                        <td className="p-3 text-muted_ink">{r.email}</td>
                        <td className="p-3">
                          <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium uppercase tracking-wider ${
                            r.status === "yes" ? "bg-sage/20 text-sage-dark" : r.status === "maybe" ? "bg-gold/20 text-gold" : "bg-shell text-muted_ink"
                          }`}>{r.status}</span>
                        </td>
                        <td className="p-3 text-muted_ink">{r.lodging}</td>
                        <td className="p-3 text-muted_ink">{r.dietary || "-"}</td>
                        <td className="p-3 text-muted_ink max-w-xs truncate">{r.notes || "-"}</td>
                        <td className="p-3 text-muted_ink whitespace-nowrap">{new Date(r.created_at).toLocaleDateString()}</td>
                        <td className="p-3">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => openEdit(r)}
                              className="text-muted_ink hover:text-sage-dark transition-colors p-1"
                              title="Edit RSVP"
                              data-testid={`rsvp-edit-${r.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => deleteRsvp(r.id, r.guest_name)}
                              className="text-muted_ink hover:text-terracotta transition-colors p-1"
                              title="Delete RSVP"
                              data-testid={`rsvp-delete-${r.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="rooms" className="mt-6">
            <div className="space-y-6">
              {Object.entries(roomsByType).map(([type, list]) => (
                <div key={type} className="bg-sand border border-shell rounded-lg overflow-hidden">
                  <div className="p-4 bg-ink text-cream flex items-center justify-between">
                    <h3 className="font-display text-xl font-bold">{type}</h3>
                    <p className="text-xs text-cream/70">{list.length} rooms</p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-shell/50 text-ink">
                        <tr>
                          <th className="text-left p-3">#</th>
                          <th className="text-left p-3">Status</th>
                          <th className="text-left p-3">Claimed by</th>
                          <th className="text-left p-3">Email</th>
                          <th className="text-left p-3">+1</th>
                          <th className="text-left p-3">Phone</th>
                          <th className="text-left p-3">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {list.map((r) => (
                          <tr key={r.id} className="border-t border-shell" data-testid={`room-row-${r.id}`}>
                            <td className="p-3 font-medium">{r.room_number}</td>
                            <td className="p-3">
                              <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium uppercase tracking-wider ${
                                r.status === "open" ? "bg-sage/20 text-sage-dark" :
                                r.status === "pending" ? "bg-terracotta/20 text-terracotta" :
                                "bg-ink text-cream"
                              }`}>{r.status}</span>
                            </td>
                            <td className="p-3 text-muted_ink">{r.claimed_by?.guest_name || "-"}</td>
                            <td className="p-3 text-muted_ink">{r.claimed_by?.email || "-"}</td>
                            <td className="p-3 text-muted_ink">{r.claimed_by?.partner_name || "-"}</td>
                            <td className="p-3 text-muted_ink">{r.claimed_by?.phone || "-"}</td>
                            <td className="p-3 space-x-1">
                              {r.status !== "open" && <button onClick={() => updateStatus(r.id, "open")} className="text-xs px-2 py-1 rounded bg-cream border border-shell hover:bg-shell" data-testid={`room-open-${r.id}`}>Open</button>}
                              {r.status !== "pending" && <button onClick={() => updateStatus(r.id, "pending")} className="text-xs px-2 py-1 rounded bg-terracotta/20 border border-terracotta/30 text-terracotta hover:bg-terracotta/30" data-testid={`room-pending-${r.id}`}>Pending</button>}
                              {r.status !== "confirmed" && <button onClick={() => updateStatus(r.id, "confirmed")} className="text-xs px-2 py-1 rounded bg-ink text-cream hover:bg-ink/90" data-testid={`room-confirm-${r.id}`}>Confirm</button>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        {loading && <p className="text-center text-muted_ink mt-8">Loading…</p>}
      </div>

      {/* Edit RSVP dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-cream border-shell" data-testid="edit-rsvp-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black text-ink">
              Edit RSVP
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={saveEdit} className="space-y-4 mt-2">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Name</Label>
                <Input
                  value={editForm.guest_name || ""}
                  onChange={(e) => setEditForm({ ...editForm, guest_name: e.target.value })}
                  data-testid="edit-rsvp-name"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Partner / +1</Label>
                <Input
                  value={editForm.partner_name || ""}
                  onChange={(e) => setEditForm({ ...editForm, partner_name: e.target.value })}
                  placeholder="Leave blank to clear"
                  data-testid="edit-rsvp-partner"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input
                type="email"
                value={editForm.email || ""}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                data-testid="edit-rsvp-email"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Status</Label>
                <select
                  value={editForm.status || "yes"}
                  onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                  className="flex h-9 w-full rounded-md border border-shell bg-cream px-3 py-1 text-sm"
                  data-testid="edit-rsvp-status"
                >
                  <option value="yes">Yes</option>
                  <option value="maybe">Maybe</option>
                  <option value="no">No</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <Label>Lodging</Label>
                <select
                  value={editForm.lodging || "tbd"}
                  onChange={(e) => setEditForm({ ...editForm, lodging: e.target.value })}
                  className="flex h-9 w-full rounded-md border border-shell bg-cream px-3 py-1 text-sm"
                  data-testid="edit-rsvp-lodging"
                >
                  <option value="villa_taina">Villa Taina</option>
                  <option value="other_hotel">Other hotel</option>
                  <option value="airbnb">Airbnb</option>
                  <option value="tbd">TBD</option>
                </select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Dietary</Label>
              <Input
                value={editForm.dietary || ""}
                onChange={(e) => setEditForm({ ...editForm, dietary: e.target.value })}
                data-testid="edit-rsvp-dietary"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Input
                value={editForm.notes || ""}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                data-testid="edit-rsvp-notes"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="flex-1 h-10 rounded-full border border-shell bg-cream text-ink font-medium hover:bg-shell"
                data-testid="edit-rsvp-cancel"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary flex-1 h-10"
                data-testid="edit-rsvp-save"
              >
                Save
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
